import { useMemo } from "react";
import { Bar } from "@visx/shape";
import { ScaleLinear, ScaleTime } from "d3";

import Typography from "ds/components/Typography";

import { WorkerUsageDay } from "../types";
import {
  SPIKE_LABEL_CHAR_WIDTH,
  SPIKE_LABEL_GAP,
  SPIKE_LABEL_MIN_Y,
  SPIKE_LABEL_OFFSET,
} from "./constants";
import styles from "./styles.module.css";

type ConcurrencyChartBarsProps = {
  days: WorkerUsageDay[];
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  dayBandWidth: number;
  xMax: number;
  yMax: number;
  yAxisCap: number;
  hoveredDay?: number;
};

// One bar per recorded day. Days above the y-axis cap render as capped "spike"
// bars with a callout carrying the real peak.
const ConcurrencyChartBars = ({
  days,
  xScale,
  yScale,
  dayBandWidth,
  xMax,
  yMax,
  yAxisCap,
  hoveredDay,
}: ConcurrencyChartBarsProps) => {
  // Clustered spikes would collide, so labels are placed biggest-first and any
  // that would overlap is dropped — its bar still shows the peak on hover.
  const spikeLabelDays = useMemo(() => {
    const placed: Array<{ min: number; max: number }> = [];
    const shown = new Set<number>();
    days
      .filter((point) => point.max > yAxisCap)
      .map((point) => {
        const halfWidth = (`↑${point.max}`.length * SPIKE_LABEL_CHAR_WIDTH) / 2 + SPIKE_LABEL_GAP;
        return {
          time: point.day.getTime(),
          value: point.max,
          center: xScale(point.day),
          halfWidth,
        };
      })
      .sort((a, b) => b.value - a.value)
      .forEach((spike) => {
        const min = spike.center - spike.halfWidth;
        const max = spike.center + spike.halfWidth;
        if (placed.every((box) => max <= box.min || min >= box.max)) {
          placed.push({ min, max });
          shown.add(spike.time);
        }
      });
    return shown;
  }, [days, yAxisCap, xScale]);

  return (
    <>
      {days.map((point) => {
        // Edge days sit on the padding, so clamp them inside the plot.
        const rawBarX = xScale(point.day) - dayBandWidth / 2;
        const barX = Math.max(rawBarX, 0);
        const barWidth = Math.min(rawBarX + dayBandWidth, xMax) - barX;
        const isSpike = point.max > yAxisCap;
        const cappedValue = Math.min(point.max, yAxisCap);
        const barTop = yScale(cappedValue);
        const barHeight = Math.max(yMax - barTop, 1);
        const isHovered = hoveredDay === point.day.getTime();
        const isDimmed = hoveredDay !== undefined && !isHovered;

        // The hovered bar keeps its resting fill and the others dim: in dark
        // mode this series' hovered and inactive tokens are the same color,
        // so a hover fill would leave every bar looking identical.
        let fill = isSpike
          ? "var(--concurrency-chart-spike-color)"
          : "var(--concurrency-chart-range-bar-color)";

        if (isDimmed) {
          fill = isSpike
            ? "var(--semantic-color-background-interactive-data-non-semantic-12-inactive)"
            : "var(--semantic-color-background-interactive-data-non-semantic-6-inactive)";
        }

        return (
          <g key={point.day.getTime()}>
            <Bar
              x={barX}
              y={barTop}
              width={barWidth}
              height={barHeight}
              rx={2}
              fill={fill}
              pointerEvents="none"
            />
            {isSpike && spikeLabelDays.has(point.day.getTime()) && (
              <text
                x={xScale(point.day)}
                y={Math.max(barTop - SPIKE_LABEL_OFFSET, SPIKE_LABEL_MIN_Y)}
                textAnchor="middle"
                className={styles.spikeLabel}
                aria-hidden
              >
                <Typography tag="tspan" variant="p-body4">
                  ↑{point.max}
                </Typography>
              </text>
            )}
          </g>
        );
      })}
    </>
  );
};

ConcurrencyChartBars.displayName = "UsageWorkers.ConcurrencyChart.Bars";

export default ConcurrencyChartBars;
